import React, { useCallback, useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "../../../axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Button from "../../../components/Button/Button";
import Modal from "../../../components/Modal/Modal";
import FormikControl from "../../../components/FormikControl/FormikControl";
import EditButton from "../../../components/EditButton/EditButton";
import DeleteButton from "../../../components/DeleteButton/DeleteButton";
import Spinner from "../../../components/Spinner/Spinner";
import DialogBox from "../../../components/DialogBox/DialogBox";

import styles from "./Index.module.css";

import AddNewIcon from "../../../assets/images/add-new-icon.svg";

const Index = (props) => {
  const [products, setProducts] = useState([]);
  const [formValues, setFormValues] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDeleteDialogBox, setShowDeleteDialogBox] = useState(false);
  const [activeItem, setActiveItem] = useState(null);

  const fetchProducts = useCallback(() => {
    axios
      .get(
        `/users/${props.userId}/products?broker_id=${props.broker.id}&page=1&per-page=999`
      )
      .then((response) => {
        setProducts(response.data);
      })
      .catch((error) => {
        if (error.response.data.code === 2001) {
          const errors = error.response.data.errors;
          toast.error(Object.values(errors[0])[0]);
        } else {
          toast.error(error.response.data.message);
        }
      });
  }, [props]);

  const handleNewForm = () => {
    const formValues = {
      broker_id: "",
      broker_heading_id: "",
      invested: "",
      current_balance: "",
      withdrawn: "",
      realised_p_l: "",
      unrealised_p_l: "",
      absolute_returns: "",
    };

    setFormValues(formValues);
    setEditMode(false);
    setShowNewForm(!showNewForm);
  };

  const handleEditForm = (id) => {
    axios
      .get(`/users/${props.userId}/products/${id}`)
      .then((response) => {
        const formValues = {
          ...response.data,
        };

        setFormValues(formValues);
        setEditMode(true);
        setShowNewForm(true);
      })
      .catch((error) => {
        if (error.response.data.code === 2001) {
          const errors = error.response.data.errors;
          toast.error(Object.values(errors[0])[0]);
        } else {
          toast.error(error.response.data.message);
        }
      });
  };

  const initialValues = {
    broker_heading_id:
      formValues && formValues.heading ? formValues.heading.id : "",
    invested: formValues ? formValues.invested : "",
    current_balance: formValues ? formValues.current_balance : "",
    withdrawn: formValues ? formValues.withdrawn : "",
    realised_p_l: formValues ? formValues.realised_p_l : "",
    unrealised_p_l: formValues ? formValues.unrealised_p_l : "",
    absolute_returns: formValues ? formValues.absolute_returns : "",
  };

  const validationSchema = Yup.object({
    broker_heading_id: Yup.string().required("Heading is required"),
    invested: Yup.string().required("Invested is required"),
    current_balance: Yup.string().required("Current Balance is required"),
  });

  const onSubmit = (values) => {
    setLoading(true);

    const formData = {
      broker_id: props.broker.id,
      broker_heading_id: values.broker_heading_id,
      invested: values.invested,
      current_balance: values.current_balance,
      withdrawn: values.withdrawn,
      realised_p_l: values.realised_p_l,
      unrealised_p_l: values.unrealised_p_l,
      absolute_returns: values.absolute_returns,
    };

    if (!editMode) {
      axios
        .post(`/users/${props.userId}/products`, formData)
        .then((response) => {
          toast.success(response.data.message);
          fetchProducts();
          setShowNewForm(false);
        })
        .catch((error) => {
          if (error.response.data.code === 2001) {
            const errors = error.response.data.errors;
            toast.error(Object.values(errors[0])[0]);
          } else {
            toast.error(error.response.data.message);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      axios
        .put(`/users/${props.userId}/products/${formValues.id}`, formData)
        .then((response) => {
          toast.success(response.data.message);
          fetchProducts();
          setShowNewForm(false);
        })
        .catch((error) => {
          if (error.response.data.code === 2001) {
            const errors = error.response.data.errors;
            toast.error(Object.values(errors[0])[0]);
          } else {
            toast.error(error.response.data.message);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  // load Broker Headings
  const loadBrokerHeadings = (inputValue) => {
    return axios
      .get(
        `/brokers-headings?broker_id=${props.broker.id}&page=1&per-page=999&keyword=${inputValue}`
      )
      .then((res) => res.data);
  };

  const newForm = showNewForm ? (
    <Modal
      show={showNewForm}
      modalClosed={handleNewForm}
      title={`${editMode ? "EDIT" : "ADD"}
        Procuct`}
    >
      <div className="FormContainer ModalForm">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ isSubmitting, setFieldValue }) => {
            return (
              <Form>
                <div className={["FormBody", styles.FormBody].join(" ")}>
                  <FormikControl
                    control="selectAsync"
                    label="Heading"
                    name="broker_heading_id"
                    value={editMode ? formValues.heading : ""}
                    setFieldValue={setFieldValue}
                    loadOptions={loadBrokerHeadings}
                  />
                  <FormikControl
                    control="input"
                    type="text"
                    label="Invested *"
                    name="invested"
                  />
                  <FormikControl
                    control="input"
                    type="text"
                    label="Current Balance *"
                    name="current_balance"
                  />
                  <FormikControl
                    control="input"
                    type="text"
                    label="Withdrawn *"
                    name="withdrawn"
                  />
                  <FormikControl
                    control="input"
                    type="text"
                    label="Realised P/L *"
                    name="realised_p_l"
                  />
                  <FormikControl
                    control="input"
                    type="text"
                    label="Unrealised P/L *"
                    name="unrealised_p_l"
                  />
                  <FormikControl
                    control="input"
                    type="text"
                    label="Absolute Returns (%) *"
                    name="absolute_returns"
                  />
                </div>
                <div className="FormActions">
                  <Button
                    btnType="Primary"
                    type="Submit"
                    disabled={isSubmitting}
                  >
                    Save
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </Modal>
  ) : null;

  const handleDelete = (id = null) => {
    setShowDeleteDialogBox(!showDeleteDialogBox);
    setActiveItem(id);
  };

  const confirmDelete = () => {
    axios
      .delete(`/users/${props.userId}/products/${activeItem}`)
      .then((response) => {
        toast.success(response.data.message);
        setShowDeleteDialogBox(false);
        fetchProducts();
      })
      .catch((error) => {
        if (error.response.data.code === 2001) {
          const errors = error.response.data.errors;
          toast.error(Object.values(errors[0])[0]);
        } else {
          toast.error(error.response.data.message);
        }
      });
  };

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  return (
    <div>
      <div className={styles.Header}>
        <div className={styles.Broker}>{props.broker.name}</div>
        <div className={styles.TableActions}>
          <Button btnType="Success" clicked={handleNewForm}>
            <img src={AddNewIcon} alt="New" /> <span>Add</span>
          </Button>
        </div>
      </div>
      {products.length ? (
        <div className={styles.Products}>
          <table>
            <thead>
              <tr>
                <th>Product Name</th>
                <th>Invested</th>
                <th>Current Balance</th>
                <th>Withdrawn</th>
                <th>Realised P/L</th>
                <th>Unrealised P/L</th>
                <th>Absolute Returns (%)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {products.map((item) => {
                return (
                  <tr>
                    <td>{item.heading}</td>
                    <td>Rs. {item.invested}</td>
                    <td>Rs. {item.current_balance}</td>
                    <td>Rs. {item.withdrawn}</td>
                    <td>Rs. {item.realised_p_l}</td>
                    <td>Rs. {item.unrealised_p_l}</td>
                    <td>{item.absolute_returns}</td>
                    <td className={styles.Actions}>
                      <EditButton clicked={() => handleEditForm(item.id)} />
                      <DeleteButton clicked={() => handleDelete(item.id)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {newForm}
      <Spinner isLoading={loading} />
      <ToastContainer />
      <DialogBox
        show={showDeleteDialogBox}
        confirm={confirmDelete}
        cancel={handleDelete}
        title="Delete Product Entry"
        message="Are you sure you want to delete this Product Entry?"
      />
    </div>
  );
};

export default Index;
